import React from 'react';
import { Pagination, Typography } from '@mui/material';
import { BlocksRenderer, type BlocksContent } from '@strapi/blocks-react-renderer';
import { Preloader } from '@vedmarket/ui-kit';
import cx from 'classnames';

import { COLORS } from '../../../../config/constants';
import SearchArticle from './components/searchArticle';
import './searchResults.scss';

type SearchResultsProps = {
    articles?: HelpArticle[];
    categoryName?: string;
    isLoading?: boolean;
    page?: number;
    pageCount?: number;
    onPageChange?: (event: React.ChangeEvent<unknown>, page: number) => void;
};

const getPreviewBlocks = (body: HelpArticleBodyBlock[] = []): BlocksContent => {
    const firstPreviewBlock = body.find((block) => (
        block.type === 'paragraph' || block.type === 'list'
    ))

    return firstPreviewBlock ? [firstPreviewBlock] as BlocksContent : []
}

const blocksComponents = {
    paragraph: ({ children }: { children?: React.ReactNode }) => (
        <>{children}</>
    ),
    heading: ({ children }: { children?: React.ReactNode; level: number }) => (
        <>{children}</>
    ),
    list: ({ children, format }: { children?: React.ReactNode; format: 'ordered' | 'unordered' }) => {
        const Component = format === 'ordered' ? 'ol' : 'ul'

        return (
            <Component>
                {children}
            </Component>
        )
    },
    'list-item': ({ children }: { children?: React.ReactNode }) => (
        <li>{children}</li>
    ),
    link: ({ children, url }: { children?: React.ReactNode; url: string }) => (
        <a href={url} style={{ color: COLORS.BLUE }}>
            {children}
        </a>
    ),
}

const modifiersComponents = {
    bold: ({ children }: { children: React.ReactNode }) => <b>{children}</b>,
    underline: ({ children }: { children: React.ReactNode }) => <u>{children}</u>,
    strikethrough: ({ children }: { children: React.ReactNode }) => <s>{children}</s>,
    code: ({ children }: { children: React.ReactNode }) => <code>{children}</code>,
}

const SearchResults = ({
    articles = [],
    categoryName,
    isLoading,
    page,
    pageCount,
    onPageChange,
}: SearchResultsProps) => {

    const hasArticles = Boolean(articles.length)

    if (isLoading) {
        return (
            <div className='search-articles'>
                <Preloader/>
            </div>
        )
    }

    return (
        <div className={cx('search-articles', {
            'search-articles_hasArticles': hasArticles,
        })}>
            <div>
                {articles.map((article, index) => (
                    <SearchArticle
                        key={article.documentId}
                        number={index + 1}
                        category={categoryName ? `${categoryName} → ${article.title}` : article.title}
                        title={article.subtitle}
                        documentId={article.documentId}
                        text={
                            <BlocksRenderer
                                content={getPreviewBlocks(article.body)}
                                blocks={blocksComponents}
                                modifiers={modifiersComponents}
                            />
                        }
                    />
                ))}
                {!hasArticles && (
                    <Typography variant='subtitle1' sx={{ color: COLORS.BLACK }}>
                        Ничего не найдено
                    </Typography>
                )}
            </div>
            {Boolean(pageCount && pageCount > 1) && (
                <Pagination
                    page={page}
                    count={pageCount}
                    onChange={onPageChange}
                    sx={{
                        marginTop: 5,
                    }}
                />
            )}
        </div>
    )
};

export default SearchResults;
